// @flow
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import React from 'react';
import AppsUtil from '../../utils/AppsUtil';
import DownloadUtil from '../../utils/DownloadUtil';
import RestApiUtil from '../../utils/RestApiUtil';
import {isTestExperience} from '../../utils/TestUtil';
import Url from '../../utils/Url';
import CCWebAnalytics from '../../analytics/CCWebAnalytics';
import IncompatibleDialogUtil from '../../utils/IncompatibleDialogUtil';
import uuid from 'uuid';
import thorUtil from '../../utils/wam/ThorUtil';
import thorCommonUtil from '../../utils/wam/ThorCommonUtil';
import {thorConfirmationTypes, wamMsgStatusType, thorAppsStatesCategory} from '../../constants/wamConstants';
import {ACTION_ID, APP_PLATFORM} from '@ccxi/ccxi-apps/src/utils/modelMapper/appModelMapper/appModelConstants';
import {actionIds, campaignId, pdpActionIds,productIdToSapCode,buttonData,ffcPath,FFCLocaleMap, eventSourceMap, sapCodeToProductMetaData, twpSapcodes} from '../../constants/appsConstants';
import {ENTITLEMENTS} from '@ccxi/ccxi-apps/src/utils/modelMapper/appModelMapper/appJsonConstants';
import DescriptionDialog from '@ccxi/ccxi-apps/src/utils/modelMapper/dialogs/descriptionDialog';
import InlineProductModel from '../../containers/Inline/InlineProductModel';
import ConfirmationDialog from '@ccxi/ccxi-apps/src/utils/modelMapper/appModelMapper/confirmationDialog';
import {SKELETON_CTA_KEYS, ANALYTICS, INFORMATION_DIALOG_ID} from '@ccxi/ccxi-apps/src/utils/enums';
import {SendLinkDialog} from '@ccxi/ccxi-apps/src/stores/dialog';
import {SendLinkFunctionParams, sendLink} from '@ccxi/ccxi-apps/src/utils/pdpUtils/sendLinkMobileDevice';
import {PDP_SECTION, PDP_ACTION, HELP_SUPPORT_LINK, INFORMATION_FIELD_TYPES, SOCIAL_LINKS} from '@ccxi/ccxi-apps/src/utils/pdpUtils/pdpEnums';
import {error} from '../../components/ErrorToast/ErrorToast';
import getConfigApps from '../../config';
import {AppsPanelSkeleton} from '@ccxi/ccxi-apps/src/stores/skeleton';
import TranslationUtil from '@ccxi/ccxi-apps/src/utils/translation';
import {getLocalizedURL} from '@intl/adobe-locales';
import {clientTokenExpiryCheck} from '../../utils/ImsUtils';
import ModalContainer from '@react/react-spectrum/ModalContainer';
import CloseCircle from '@react/react-spectrum/Icon/CloseCircle';
import {interpretKey} from '@ccxi/ccxi-apps/src/utils/eventUtil';

class AppHandlers {
	history: Object;
	componentParams: Object;
	sendLinkDialog: SendLinkDialog;
	sendLinkType: string;
	sendLinkAnalyticObj: Object;
	setIsSendLinkInProgress: Function;

	/**
	 * Initializes Apps Util
	 *
	 */
	init() {

	}

	launchPDPFromCategoryPage(sat: Object,source: ?string) {
		this.history.push(AppsUtil.getCCWebURL(sat.sapcode,sat.platform,false,false,source));
	}

	goToRedirectUrl(source: ?string, sapCode: string) {
		if (source !== 'pdp') {
			this.history.push(AppsUtil.getInstallationModalUrl({
				sapCode,
				source
			}));
		}
		else {
			this.history.push(AppsUtil.getCCWebURL(sapCode, undefined, true, false, source));
		}
	}

	handlePDPAndInstallDialog(actionId: string, sat: Object, source: ?string) {
		if (!AppsUtil.allVersionsFFCProductList[sat.sapcode] && sat.sapcode !== 'KCCC' && !AppsUtil.isMobileOrTab()) {
			this.showWaiting(sat, true);
			const promiseArray = [];
			if (!AppsUtil.isFFCFallback) {
				promiseArray.push(RestApiUtil.fetchFFCAllVersionData(sat.sapcode));
			}
			else {
				promiseArray.push(RestApiUtil.fetchFFCAllVersionDataFallback(sat.sapcode));
			}
			Promise.all(promiseArray).then(() => {
				this.goToRedirectUrl(source, sat.sapcode);
				this.showWaiting(sat, false);
			},
			() => {
				this.goToRedirectUrl(source, sat.sapcode);
				this.showWaiting(sat, false);
			});
		}
		else {
			this.goToRedirectUrl(source, sat.sapcode);
		}
	}

	handleAppAction(actionId: string, sat: Object, config: Object) {
		const source = config ? config.source : null;
		if (actionId === ACTION_ID.APP_TILE_CLICK
			&& thorUtil.isActionSupportedOnProduct(sat.sapcode, sat.codexVersion, ACTION_ID.OPEN)) {
			const thorProduct = thorUtil.getWAMClient().getProduct(sat.sapcode, sat.codexVersion);
			if (!thorProduct
				|| !thorProduct.entitlement_info
				|| thorProduct.entitlement_info.status === ENTITLEMENTS.TRIAL_EXPIRED
				|| thorProduct.entitlement_info.status === ENTITLEMENTS.LICENSE_EXPIRED
				|| thorProduct.entitlement_info.status === ENTITLEMENTS.TRIAL_AVAILABLE) { // This is exclude users where trial is not active
				this.launchPDPFromCategoryPage(sat,source);
				return;
			}
			// Open with dialog functionality
			const inlineProductModel = new InlineProductModel(sat.sapcode, sat.codexVersion);
			if (inlineProductModel) {
				const showDialog = true;
				inlineProductModel.launch(showDialog);
				return;
			}
		}
		if (actionId === ACTION_ID.NOT_COMPATIBLE || actionId === ACTION_ID.LEARN_MORE_PDP || actionId === 'enterprise') {
			IncompatibleDialogUtil.renderDialog(sat);
			return;
		}
		if (AppsUtil.hasFeature('temp_send_link_for_desktop_apps') && AppsUtil.isMobileOrTab() && sat.platform.toLowerCase() === APP_PLATFORM.DESKTOP && (actionId === 'download' || actionId === ACTION_ID.TRY || actionId === 'send-link')) {
			this.openSendMeLinkDialog(
				'send-link',
				AppsUtil.CCXUIAppStore[`${sat.sapcode}_${AppsUtil.getFFCProductConfigBySapCode(sat.sapcode).version}`],
				{source}
			);
			return;
		}
		if (actionId === ACTION_ID.LEARN_MORE || actionId === 'appTileSelect' || actionId === ACTION_ID.APP_TILE_CLICK) {
			// launch pdp
			this.launchPDPFromCategoryPage(sat,source);
			return;
		}
		if (actionId === ACTION_ID.BUY || actionId === ACTION_ID.BUY_NOW) {
			this.openExternalLink(AppsUtil.goURL(`${buttonData.buy.linkURL}${sat.sapcode}`), sat.sapcode, config.guid);
			return;
		}

		if ((isTestExperience(campaignId.trialToTWPFree) || isTestExperience(campaignId.trialToTWPPaid))
			&& (actionId === ACTION_ID.TRY || actionId === actionIds.tryOldVersion)
			&& twpSapcodes.indexOf(sat.sapcode) > -1) {
			AppsUtil.addMiniPlanModal(sat.sapcode, AppsUtil.locale, () => {}, () => {
				this.plansModalOnClose({
					name: sat.appFamilyName,
					version: sat.codexVersion,
					sapCode: sat.sapcode,
					platform: sat.platform,
					source: config.source
				});
			}, '',
			(url) => {
				this.plansModalOnLoad({
					name: sat.appFamilyName,
					version: sat.codexVersion,
					sapCode: sat.sapcode,
					platform: sat.platform,
					url,
					source: config.source
				});
			});
			return;
		}
		else if (sat.platform.toLowerCase() === APP_PLATFORM.DESKTOP) {
			if ((thorUtil.isConnected() || thorCommonUtil.onceExecutedViaWAMWorkflow())
				&& thorUtil.isActionSupportedOnProduct(sat.sapcode, sat.codexVersion, actionId)
				&& thorUtil.isCategorySupported({sapcode: sat.sapcode, version: sat.codexVersion})) {
				const inlineProductModel = new InlineProductModel(sat.sapcode, sat.codexVersion, sat.appFamilyName, thorUtil.isProductEntitled(thorUtil.getWAMClient().getProduct(sat.sapcode, sat.codexVersion)));
				inlineProductModel.fallBackOnFailure = () => {
					this.handlePDPAndInstallDialog(actionId, sat, source);
				};
				switch (actionId) {
					case ACTION_ID.INSTALL:
					// break omitted
					case ACTION_ID.TRY:
						inlineProductModel.install();
						thorCommonUtil.setWaiting(sat.sapcode, sat.codexVersion);
						break;
					case ACTION_ID.OPEN:
						inlineProductModel.launch();
						break;
					case ACTION_ID.START_TRIAL:
						inlineProductModel.startTrial();
						break;
					case ACTION_ID.UNINSTALL:
						inlineProductModel.uninstall();
						thorCommonUtil.setWaiting(sat.sapcode, sat.codexVersion);
						break;
					case ACTION_ID.CANCEL:
					// break omitted
					case ACTION_ID.PROGRESS_CANCEL:
						inlineProductModel.cancel();
						break;
					case ACTION_ID.UPDATE:
						inlineProductModel.update();
						thorCommonUtil.setWaiting(sat.sapcode, sat.codexVersion);
						break;
					case ACTION_ID.RETRY:
						inlineProductModel.retry();
						break;
					case ACTION_ID.ERROR_INFO:
						{
							const thorProduct = thorUtil.getWAMClient().getProduct(sat.sapcode, sat.codexVersion);
							const confirmationID = uuid.v4();
							thorProduct.display_name = sat.appFamilyName; // eslint-disable-line camelcase
							thorProduct.confirmationDetails = {
								confirmationID,
								confirmationType: thorConfirmationTypes.retryableError,
								status: wamMsgStatusType.needConfirmation,
								confirmationSAPCode: sat.sapcode,
								confirmationVersion: sat.codexVersion,
							};
							thorProduct.status = wamMsgStatusType.needConfirmation;
							if (thorProduct.error_details && thorProduct.error_details.error_code) {
								ConfirmationDialog.updateDialogState(thorProduct, (actionId) => {
									if (actionId === 'done') {
										thorProduct.confirmationDetails.status = wamMsgStatusType.closeConfirmation;
										thorProduct.status = wamMsgStatusType.closeConfirmation;
										ConfirmationDialog.showOrHideDialog(thorProduct);
									}
								});
								ConfirmationDialog.showOrHideDialog(thorProduct);
							}
						}
						break;
					case ACTION_ID.UPDATE_STATUS:
						this.history.push('/apps/updates');
						break;
					case ACTION_ID.SEE_MORE:
						if (config.seeMoreLinkUrl
							&& sat.productWhatsNewUrl
							&& sat.isUpdateAvailable) {
							// open external URL
							this.openExternalLink(sat.productWhatsNewUrl, sat.sapcode, config.guid);
						}
						else {
							// show dialog
							DescriptionDialog.show(sat, config.localizedUpdateDescription);
						}
						break;
				}
				return;
			}
			this.handlePDPAndInstallDialog(actionId, sat, source);
		}
		else {
			if (sat.platform.toLowerCase() === APP_PLATFORM.WEB) {
				if (AppsUtil.appsFFCServiceList[sat.sapcode]) {
					let url = AppsUtil.appsFFCServiceList[sat.sapcode].serviceUrl;
					if (AppsUtil.hasFeature('temp_web_apps_ffc_localized')) {
						url = getLocalizedURL(url, AppsUtil.locale);
					}
					this.openExternalLink(url, sat.sapcode, config.guid, source);
				}
			}
			else if (sat.platform.toLowerCase() === APP_PLATFORM.MOBILE) {
				if (AppsUtil.appsFFCMobileList[sat.sapcode]) {
					if (!AppsUtil.isMobileOrTab() && actionId === 'send-link') {
						// use source.categoryId to populate analytic
						// object used for send link dialog events such
						// as button clicks, send link failure/success.
						this.openSendMeLinkDialog(actionId, sat, config);
					}
					else {
						this.openExternalLink(AppsUtil.appsFFCMobileList[sat.sapcode].mobileAppUrl, sat.sapcode, config.guid);
					}
				}
			}
		}
	}

	openExternalLink(url: string, sapCode: ?string, guid: string, source: ?string) {
		const connector = url.indexOf('?') > -1 ? '&' : '?';
		let extUrl = `${url}${connector}x-product=CCHome&x-product-location=Apps&guid=${guid}`;
		if (sapCode === 'stock-web' || sapCode === 'FFC_stock' || url.indexOf('stock') !== -1) {
			extUrl = AppsUtil.addParamsToStockUrl(extUrl, source);
		}
		// In case of Adobe fonts launch URL, modifying url coming from ffc to replace and append CCHome specific attribution data in the pathname.
		if (sapCode === 'FFC_fonts') {
			const path = extUrl.split('/');
			const slug = path[4].split('?');
			extUrl = extUrl.replace(slug[0],'cd932b4812');
		}
		extUrl = extUrl.match(/^http[s]?:\/\//i) ? extUrl : `https://${extUrl}`;
		window.open(extUrl, '_blank');
	}

	showWaiting(sat: Object, show: boolean) {
		// isInProgress, isWaiting, progressvalue , localized text, is cancellable, cancle callback
		if (show) {
			AppsUtil.CCXUIAppStore[`${sat.sapcode}_${sat.codexVersion}`].updateProgress(false, show, undefined, TranslationUtil.getMessage('ccx.appAction.status.waiting'), false);
		}
		else {
			AppsUtil.CCXUIAppStore[`${sat.sapcode}_${sat.codexVersion}`].resetProgress();
		}
	}

	handleSkeletonItemClick(action: string, event: Object, urlObj: Object) {
		if (action === SKELETON_CTA_KEYS.UPDATE_ALL_KEY) {
			if (thorUtil.isUpdateAllSupported()) {
				const inlineProductModel = new InlineProductModel(thorAppsStatesCategory.updateAllConfirmation, thorAppsStatesCategory.updateAllConfirmation);
				inlineProductModel.updateAll();
			}
			return;
		}
		if (event && urlObj) {
			event.preventDefault();
			event.stopPropagation();
			this.openExternalLink(urlObj.url, undefined, urlObj.guid, 'resources');
		}
	}

	openCreativeCloud() {
		if (AppsUtil.hasThor) {
			DownloadUtil.routeToCCDPath('/apps/updates');
		}
		else {
			this.history.push(AppsUtil.getInstallationModalUrl({
				sapCode: 'KCCC',
				source: 'updates'
			}));
		}
	}

	handlePDPVideo(config: Object) {
		const videoSourceList = config.videoList.map(
			(item, index) => <source src={item.src} type={item.type} key={index} />
		);
		const videoDialogId = ModalContainer.show(
			<div className='banner-video-dialog'>
				<CloseCircle
					size='M'
					aria-label={AppsUtil.intl.formatMessage({id: 'apps.pdp.video.banner.close'})}
					role='button'
					onKeyDown={interpretKey({
						onEnter: [
							() => {
								ModalContainer.hide(videoDialogId);
								config.callbacks.onCloseCallback();
							},
							true
						],
					})}
					onClick={() => {
						ModalContainer.hide(videoDialogId);
						config.callbacks.onCloseCallback();
					}}
					className='close'
					tabIndex='0' />

				{ /* eslint-disable jsx-a11y/media-has-caption */}
				<video
					className='banner-video'
					disablePictureinPicture={config.config.disablePictureinPicture}
					controls
					autoPlay
					onPause={() => config.callbacks.onPauseCallback()}
					onPlay={() => config.callbacks.onPlayCallback()}
					onLoadStart={() => config.callbacks.onLoadStartCallback()}
					controlsList='nodownload'>
					{videoSourceList}
				</video>
				{ /* eslint-enable jsx-a11y/media-has-caption */}
			</div>, null, document.querySelector('.banner-video-modal-target')
		);
	}

	handlePDPAction(e: Function, sectionid: string, actionId: string, config: Object) {
		// console.log(`Handling PDP events for ${sectionid} ${actionId}`);
		const pathData = AppsUtil.getCCWebPathData();
		const sapcode = productIdToSapCode[pathData.product];
		switch (actionId) {
			case PDP_ACTION.HEADING_CLICK: {
				window.location.href = window.location.href;
				break;
			}
			case INFORMATION_FIELD_TYPES.LANGUAGES: {
				AppsUtil.informationDialogStore[sapcode].defaultSelectedTabId = pdpActionIds.languages;
				AppsUtil.informationDialogStore[sapcode] && AppsUtil.informationDialogStore[sapcode].show();
				break;
			}
			case INFORMATION_FIELD_TYPES.COMPATIBILITY: {
				config.actionObject.fieldUrl && this.openExternalLink(config.actionObject.fieldUrl, undefined, config.guid);
				break;
			}
			case PDP_ACTION.BACK_CLICK: {
				if (this.history.location.state && this.history.location.state.source && (this.history.location.state.source === 'toastClick' || this.history.location.state.source === 'PDP')) {
					this.history.goBack();
				}
				else {
					this.history.push(AppsUtil.getCCWebURL());
				}
				break;
			}
			case PDP_ACTION.CATEGORY_LABEL_CLICK: {
				if (AppsUtil.isCCHInLMS()) {
					return;
				}
				this.history.push(`/apps/${config.actionObject.id}`,{source:'PDP'});
				break;
			}
			case PDP_ACTION.APP_CLICK: {
				if (sectionid === PDP_SECTION.RELATED_APPS || sectionid === PDP_SECTION.OTHER_PLATFORMS) {
					this.history.push(AppsUtil.getCCWebURL(config.actionObject.app.sapcode),{source:'PDP'});
				}
				break;
			}
			case PDP_ACTION.TUTORIAL_CLICK: {
				config.actionObject.tutorialURL && this.openExternalLink(config.actionObject.tutorialURL, undefined, config.guid);
				break;
			}
			case PDP_ACTION.CARD_CLICK: {
				config.actionObject.cardURL && this.openExternalLink(config.actionObject.cardURL, undefined, config.guid);
				break;
			}
			case PDP_ACTION.VIDEO_BANNER_CLICK: {
				this.handlePDPVideo(config);
				break;
			}
			case PDP_ACTION.VIEW_MORE_CLICK: {
				if (sectionid === PDP_SECTION.OVERVIEW) {

				}
				if (sectionid === PDP_SECTION.BEHANCE_GALLERY) {

				}
				if (sectionid === PDP_SECTION.TUTORIALS) {

				}
				// break;
			}
			// break omitted
			case HELP_SUPPORT_LINK.TROUBLESHOOTING_AND_HELP: {


			}
			// break omitted
			case HELP_SUPPORT_LINK.CONTACT_US: {


			}
			// break omitted
			case HELP_SUPPORT_LINK.TUTORIALS: {


			}
			// break omitted
			case HELP_SUPPORT_LINK.COMMUNITY_AND_FORUM: {


			}
			// break omitted
			case SOCIAL_LINKS.FACEBOOK: {


			}
			// break omitted
			case SOCIAL_LINKS.TWITTER: {


			}
			// break omitted
			case SOCIAL_LINKS.YOUTUBE: {


			}
			// break omitted
			case SOCIAL_LINKS.INSTAGRAM: {
				config.actionObject.urlData && config.actionObject.urlData.url && this.openExternalLink(config.actionObject.urlData.url, undefined, config.guid);
				break;
			}
		}
	}

	handleInformationDialogAction(e: Function, actionId: string) {
		const pathData = AppsUtil.getCCWebPathData();
		const sapcode = productIdToSapCode[pathData.product];
		switch (actionId) {
			case INFORMATION_DIALOG_ID.DONE_CLICK: {
				AppsUtil.informationDialogStore[sapcode] && AppsUtil.informationDialogStore[sapcode].hide();
				break;
			}
		}
		// console.log(`Handling PDP events for ${actionId}`);
	}

	openSendMeLinkDialog(actionId: string, sat: Object, config: Object) {
		if (!this.sendLinkDialog) {
			let subcategory = AppsPanelSkeleton.selectedSectionFilter;
			if (config && config.source === ANALYTICS.PDP) {
				subcategory = `Categories:${subcategory}`;
			}
			// set send link modal render source
			this.sendLinkAnalyticObj = {
				event: {
					workflow: 'Apps',
					subcategory,
				},
				content: {
					id: sat.sapcode,
					name: sat.appFamilyName,
					type: 'Send Link',
					category: sat.platform,
					version: sat.codexVersion,
				},
				ui: {
					view_type: eventSourceMap[config.source], // eslint-disable-line camelcase
					filter: eventSourceMap[config.source]
				}
			};
			this.componentParams = {source: config.source};
			const app = AppsUtil.CCXUIAppStore[`${sat.sapcode}_${sat.codexVersion}`];
			const supportedServices = (AppsUtil.hasFeature('temp_send_link_for_desktop_apps') && sat.platform === APP_PLATFORM.DESKTOP) ? ['email'] : ['sms','email'];
			const adobeIDEmailAddresses = [];
			let userCountry;
			if (window.adobeIMS && window.adobeIMS.getUserProfile && window.adobeIMS.getUserProfile()) {
				const userProfile = window.adobeIMS.getUserProfile();
				adobeIDEmailAddresses.push(userProfile.email);
				userCountry = userProfile.countryCode;
			}
			const onAction = this.handleSendLinkAction.bind(this);
			this.sendLinkDialog = new SendLinkDialog({app, supportedServices, adobeIDEmailAddresses, userCountry, onAction});
			this.sendLinkAnalyticsCallback({
				event: {
					type: 'render',
					subtype: 'modal',
				},
			});
			this.sendLinkDialog.show({onClose: () => {this.sendLinkDialog = null;}});
		}
	}

	handleSendLinkAction(e: Function, type: string, sapcode: string, setIsSendLinkInProgress: Function, value: string, appLink: string) {
		this.setIsSendLinkInProgress = setIsSendLinkInProgress;
		if (type === 'cancel') {
			this.sendLinkAnalyticsCallback({
				event: {
					type: 'click',
					subtype: 'modal-send-link-cancel',
				},
			});
		}
		else {
			this.sendLinkType = type;
			this.sendLinkAnalyticObj.event.value = type;
			this.sendLinkAnalyticsCallback({
				event: {
					type: 'click',
					subtype: 'modal-send-link',
				},
			});
			const ffcDomain = getConfigApps('servers').FFC;
			const ffcSMSPath = `${ffcPath.sms}?~tags=sms&~channel=CCWeb`;
			let ffcEmailPath = `${ffcPath.email}?~tags=email&~channel=CCWeb`;
			const locale = FFCLocaleMap[AppsUtil.locale];
			const adobeAppId = 'CCHomeWeb1';
			const accessToken = clientTokenExpiryCheck() && AppsUtil.getImsData().token;
			const timeout = 5000;
			let template;
			let templateParameters = {};
			let sendLinkType = type;

			const productConfig = AppsUtil.getFFCProductConfigBySapCode(sapcode);
			const isDesktopProduct = productConfig && productConfig.type && productConfig.type.toLowerCase() === 'desktop';
			if (AppsUtil.hasFeature('temp_send_link_for_desktop_apps') && isDesktopProduct) {
				sendLinkType = 'email_desktop';
				template = 'ffc_email_desktop_template';
				ffcEmailPath = ffcPath.emailDesktop;
				let countryCode;
				let userName;
				if (window.adobeIMS && window.adobeIMS.getUserProfile && window.adobeIMS.getUserProfile()) {
					countryCode = window.adobeIMS.getUserProfile().countryCode;
					userName = window.adobeIMS.getUserProfile().first_name;
				}
				templateParameters = {
					ES_GOVERNING_REGION_COUNTRY: countryCode,
					MOBILE_APP_NAME: productConfig.displayName,
					MOBILE_APP_URL: appLink ? appLink : DownloadUtil.getEmailMeLinkDownloadUrl(undefined, undefined, undefined, uuid.v4(), sapCodeToProductMetaData[sapcode].productId),
					USER_FIRST_NAME: userName
				};
			}
			const sendLinkFunParam = new SendLinkFunctionParams(
				sendLinkType,
				sapcode,
				value,
				ffcDomain,
				ffcSMSPath,
				ffcEmailPath,
				locale,
				adobeAppId,
				accessToken,
				this.handleSendLinkSuccess.bind(this),
				this.handleSendLinkError.bind(this),
				templateParameters,
				timeout,
				undefined,
				undefined,
				template
			);
			sendLink(sendLinkFunParam);
		}
		this.sendLinkDialog.hide();
		this.sendLinkDialog = null;
	}

	sendLinkAnalyticsCallback(eventAnalyticObj: Object) {
		const event = {...this.sendLinkAnalyticObj.event, ...eventAnalyticObj.event};
		const analyticObject = {event, content: this.sendLinkAnalyticObj.content, ui: this.sendLinkAnalyticObj.ui};
		this.handleAnalytics({analyticObjectList: [analyticObject], componentParams: this.componentParams});
	}

	handleSendLinkSuccess() {
		this.sendLinkAnalyticsCallback({
			event: {
				type: 'success',
				subtype: 'send-link',
			},
		});
		let msgId = 'send-link-success';
		if (this.sendLinkType === 'sms' || this.sendLinkType === 'email') {
			msgId = `send-link-success-${this.sendLinkType}`;
		}
		error(AppsUtil.intl.formatMessage({id: 'apps.sendLink.success'},{type: this.sendLinkType}), {msgId, variant:'success'});
	}

	handleSendLinkError(response: Object) {
		this.sendLinkAnalyticsCallback({
			event: {
				type: 'error',
				subtype: 'send-link',
				error_code: response.status, // eslint-disable-line camelcase
				error_desc: response.message, // eslint-disable-line camelcase
			},
		});
		error(AppsUtil.intl.formatMessage({id: 'apps.sendLink.error'}), {msgId: 'send-link-error',variant:'error'});
	}

	handleAnalytics(config: Object) {
		if (Url.getParam('action') === 'install' && config.componentParams.source === 'pdp' && (Url.getParam('isStarter') === 'true' || Url.getParam('source') !== 'pdp')) {
			return;
		}
		const ccwebAnalytics = new CCWebAnalytics(window.ENV);
		ccwebAnalytics.initAnalytics();
		ccwebAnalytics.genericAnalyticEvent(config);
	}

	plansModalOnClose = (config) => {
		const {name, version, sapCode, platform, source} = config;
		let subcategory = AppsPanelSkeleton.selectedSectionFilter;
		if (source === ANALYTICS.PDP) {
			subcategory = `Categories:${subcategory}`;
		}
		const ccwebAnalytics = new CCWebAnalytics(window.ENV);
		ccwebAnalytics.initAnalytics();
		ccwebAnalytics.genericAnalyticEvent({
			componentParams: {
				actionId: 'close',
				source: 'apps'
			},
			analyticObjectList: [{
				content: {
					category: platform,
					id: sapCode,
					name,
					version,
					type: 'TWP Modal'
				},
				event: {
					subtype: 'close',
					type: 'click',
					workflow: 'Apps',
					subcategory
				},
				ui: {
					view_type: eventSourceMap[source], // eslint-disable-line camelcase
					filter: eventSourceMap[source]
				}
			}]
		});
	}

	plansModalOnLoad = (config) => {
		const {name, version, sapCode, platform, url, source} = config;
		let subcategory = AppsPanelSkeleton.selectedSectionFilter;
		if (source === ANALYTICS.PDP) {
			subcategory = `Categories:${subcategory}`;
		}
		const ccwebAnalytics = new CCWebAnalytics(window.ENV);
		ccwebAnalytics.initAnalytics();
		ccwebAnalytics.genericAnalyticEvent({
			componentParams: {
				actionId: 'load',
				source: 'apps'
			},
			analyticObjectList: [{
				content: {
					category: platform,
					id: sapCode,
					name,
					version,
					action: url,
					type: 'TWP Modal'
				},
				event: {
					subtype: 'modal',
					type: 'render',
					workflow: 'Apps',
					subcategory
				},
				ui: {
					view_type: eventSourceMap[source], // eslint-disable-line camelcase
					filter: eventSourceMap[source]
				}
			}]
		});
	}
}

export default new AppHandlers();
